/**
 * The valuation payload of the REVA backend — mirrors `ValuationDataApiModel` (JSON root name `ValuationData`),
 * which every load, update and workflow operation returns and which reva-pdf also receives for rendering.
 *
 * Shared between client and reva-pdf so there is one description of the model instead of one per consumer.
 *
 * Two conventions follow from how the backend serializes:
 *
 *  - Nullable backend fields are **absent**, not `null` — the response mapper omits nulls. They are therefore
 *    declared optional rather than `| null`.
 *  - The parameter layers are free-form JSON (`ObjectNode`) keyed by AVM parameter name, so they stay
 *    `Record<string, any>`.
 */

import type {GeoRefAddress} from "../geo-reference";
import type {RevaLifeCycleState} from "../lifecycle-state";
import {SEGMENT} from "../reva-spec";
import type {RevaValuationPurpose} from "../valuation-purpose";
import type {ValueOrderData} from "../value-order";

/**
 * The property segments a REVA valuation can be created for. The backend accepts every `SegmentEnum` value and the
 * AVM spec covers two more (`BUERO_K`, `BUERO_M`), but REVA offers exactly these five.
 */
export const REVA_SEGMENTS = [SEGMENT.EZFH_K, SEGMENT.MFHWGH_K, SEGMENT.WHG_K, SEGMENT.WHG_M, SEGMENT.GRST_K] as const;

export type RevaSegment = (typeof REVA_SEGMENTS)[number];

/** The real address of a valuation whose working address is a reference address (see `hasReferenceAddress`). */
export interface OriginalAddress {
    street?: string;
    houseNumber?: string;
    city: string;
    zipCode: string;
}

/** One entry of the correction history — a valuation that was reopened after finalization. */
export interface ValuationCorrectionHistoryItem {
    date: string;
    user: string;
    userFullName?: string;
    comment?: string;
}

export interface ValuationMetadata {
    licenseId: number;
    licenseTitle: string;
    licenseDisplayName?: string;
    licenseValueClientId?: string;
    creationDate: string;
    creationUser: string;
    creationUserFullName?: string;
    lastUpdateDate: string;
    lastUpdateUser: string;
    lastUpdateUserFullName?: string;
    finalizeDate?: string;
    finalizeUser?: string;
    finalizeUserFullName?: string;
    firstFinalizeDate?: string;
    accountDomain?: string;
    correctionCounter?: number;
    correctionDate?: string;
    correctionUser?: string;
    correctionUserFullName?: string;
    correctionHistory?: ValuationCorrectionHistoryItem[];
    owner?: string;
    ownerFullName?: string;
    lifeCycleState: RevaLifeCycleState;
    /** Zweck der REVA-ID (#19481); fehlt bei Altdokumenten = Bewertung, daher über `effectivePurpose` lesen. */
    purpose?: RevaValuationPurpose;
    /** Zeitpunkt und Urheber des letzten Zweckwechsels (z. B. Research → Bewertung). */
    purposeChangeDate?: string;
    purposeChangeUser?: string;
    purposeChangeUserFullName?: string;
    /** Automatische Löschung ohne weitere Nutzung (`lastUpdateDate` + Frist des Zwecks); nur bei Zwecken mit Frist. */
    retentionExpiresAt?: string;
}

/**
 * A differing address was detected on a value order and adopted into the valuation, replacing the previously
 * stored one.
 */
export interface OrderAddressChangedNotification {
    notified: boolean;
    previousAddress: string;
}

/**
 * A differing (reference) address was detected on a value order but could not be adopted, because the new address
 * was not georeferenceable. The old address has been kept.
 */
export interface OrderAddressNotAdoptedNotification {
    notified: boolean;
    failedAddressQuery: string;
    wasReferenceAddress: boolean;
}

/** The valuation has more than one energy certificate and the OCR layer is still empty — the user must pick one. */
export interface EnergyCertificateDecisionRequiredNotification {
    notified: boolean;
    count: number;
}

/** Der Nutzer hat die Bewertung einer anderen Person zugewiesen und damit selbst den Zugriff verloren. */
export interface AccessLostByReassignmentNotification {
    notified: boolean;
}

/** Which layer moved the effective segment. */
export type SegmentChangeSource = "INSPECTION" | "IMMODROP";

/**
 * An external source moved the effective segment and the REVA overrides were discarded with it.
 */
export interface SegmentChangedNotification {
    notified: boolean;
    previousSegment: string;
    source: SegmentChangeSource;
}

export interface ValuationNotifications {
    accessLostByReassignment?: AccessLostByReassignmentNotification;
    orderAddressChanged?: OrderAddressChangedNotification;
    orderAddressCouldNotBeAdopted?: OrderAddressNotAdoptedNotification;
    energyCertificateDecisionRequired?: EnergyCertificateDecisionRequiredNotification;
    segmentChanged?: SegmentChangedNotification;
}

/** How a snapshot was triggered; mirrors the backend enum `SnapshotType`. */
export const SnapshotType = {
    PreFinal: "PreFinal",
    Final: "Final",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type SnapshotType = (typeof SnapshotType)[keyof typeof SnapshotType];

/** Metadata of one valuation snapshot, without the frozen valuation payload. */
export interface SnapshotMetadata {
    number: number;
    type: SnapshotType;
    comment?: string;
    date: string;
    user: string;
    userFullName?: string;
}

/**
 * Additional properties handed over with the initial valuation, in the original German field names of the
 * importing system. Immutable once the valuation exists, and therefore never frozen into a snapshot.
 *
 * The importing system is not validated against this shape, so the fields below are the ones observed,
 * not a contract. Every level therefore also admits further keys as `unknown`: the known fields stay
 * readable without a cast, and reading anything else has to state what it expects.
 */
export interface InitialValuationAdditionalProperties {
    [key: string]: unknown;
    energieEffizienzAusweis?: {
        [key: string]: unknown;
        ausweisTyp?: string;
        endEnergie?: {
            [key: string]: unknown;
            aktuell?: number;
        };
        primaerEnergieTyp?: string;
    };
    erweiterteObjektwertAngaben?: {
        [key: string]: unknown;
        altlasten?: string;
        altlastenGemaessAuskunft?: string;
        baulasten?: string;
        einschraenkungenDerVerwertbarkeit?: string;
        gebiet?: string;
        lageImOrt?: string;
        verwertbarkeit?: string;
    };
    flurstuecke?: Array<{
        [key: string]: unknown;
        anteil?: {
            [key: string]: unknown;
            anteil?: number;
            gesamt?: number;
        };
        flur?: string;
        flurstuecksNummer?: string;
        groesse?: number;
    }>;
}

export interface ValuationData {
    revaId: string;
    immobilienId?: string;
    segment: RevaSegment;
    addressQuery: string;
    address: GeoRefAddress;
    hasReferenceAddress: boolean;
    referenceAddressComment?: string;
    originalAddress?: OriginalAddress;

    /** REVA override parameters (tab "Overview") — the only layer the backend always sends. */
    parametersInReva: Record<string, any>;
    parametersInInitialValuation?: Record<string, any>;
    parametersInInspection?: Record<string, any>;
    parametersInInspectionDerived?: Record<string, any>;
    parametersInRegister?: Record<string, any>;
    parametersInEnergyCertificate?: Record<string, any>;
    /** Raw OCR extraction result for the energy certificate. */
    parametersInIdpEnergyCertificate?: Record<string, any>;
    parametersInPlot?: Record<string, any>;
    /** Raw extraction result from the last Immodrop URL/PDF scan. */
    parametersInImmodrop?: Record<string, any>;
    /** Input parameters derived from the response of the first finalization. */
    parametersInFirstFinalize?: Record<string, any>;
    parametersOutInitialValuation?: Record<string, any>;

    parameterOverrideComments?: Record<string, string>;
    parameterRegisterComments?: Record<string, string>;
    parameterEnergyCertificateComments?: Record<string, string>;
    parameterPlotComments?: Record<string, string>;
    parameterInspectionDerivedComments?: Record<string, string>;

    /** Properties that are irrelevant for the indication, e.g. appreciations or the map position. */
    documentProperties: Record<string, any>;

    initialValuationExecuted?: string;
    initialValuationAdditionalProperties?: InitialValuationAdditionalProperties;

    valueOrders: ValueOrderData[];
    metadata: ValuationMetadata;
    notifications: ValuationNotifications;

    /** True if this payload is a frozen snapshot instead of the live valuation data. */
    isSnapshot: boolean;
    /** Metadata of the snapshot; only set when `isSnapshot` is true. */
    snapshotMetadata?: SnapshotMetadata;
    /** Frozen AVM indication request of the snapshot; only set when `isSnapshot` is true. */
    snapshotParametersInValuation?: Record<string, any>;
    /** Frozen AVM indication response of the snapshot; only set when `isSnapshot` is true. */
    snapshotParametersOutValuation?: Record<string, any>;
    /**
     * Snapshot list; sent when a valuation is loaded or created and by the operations that change the snapshots
     * ((pre-)finalize) — absent on all other update responses.
     */
    snapshots?: SnapshotMetadata[];
}
